import { Badge } from "@/components/ui/badge";
import { CompanyAvatar } from "./CompanyAvatar";
import { MapPin, Briefcase, ExternalLink } from "lucide-react";
import { timeAgo } from "@/lib/timeAgo";
import {
  isCrawlerJob,
  getCompanyName,
  getCompanyLogoUrl,
  getSourceName,
  getSourceLabel,
  getSourceUrl,
  getSourceType,
  getSourceTypeBadge,
} from "@/lib/jobUtils";

const employmentTypeLabels: Record<string, string> = {
  estagio: "Estágio",
  clt: "CLT",
  pj: "PJ",
  freelancer: "Freelancer",
  aprendiz: "Aprendiz",
  junior: "Júnior",
  primeiro_emprego: "Primeiro Emprego",
  assistente: "Assistente",
  analista_junior: "Analista Junior",
};

const workModelLabels: Record<string, string> = {
  presencial: "Presencial",
  hibrido: "Híbrido",
  remoto: "Remoto",
};

const entryLevelTypes = ["aprendiz", "estagio", "junior"];

interface JobDetailHeaderProps {
  job: any;
}

export function JobDetailHeader({ job }: JobDetailHeaderProps) {
  const employmentLabel = employmentTypeLabels[job.employment_type] || job.employment_type;
  const location = [job.location_city, job.location_state].filter(Boolean).join(", ");
  const companyName = getCompanyName(job);
  const logoUrl = getCompanyLogoUrl(job);
  const isCrawler = isCrawlerJob(job);
  const sourceName = getSourceName(job);
  const sourceUrl = getSourceUrl(job);
  const sourceType = getSourceType(job);
  const isEntryLevel = entryLevelTypes.includes(job.employment_type);
  const publishedAgo = timeAgo(job.published_at);

  return (
    <div className="space-y-4">
      {/* Source indicator - prominent for crawler jobs */}
      {isCrawler && sourceName && (
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs font-normal gap-1">
            {(() => {
              const stb = getSourceTypeBadge(sourceType);
              return <span className="font-medium">{stb.label}</span>;
            })()}
            <span className="text-muted-foreground">·</span>
            <span>via {getSourceLabel(sourceName)}</span>
          </Badge>
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              Ver original <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}

      {/* Title + company */}
      <div className="flex items-start gap-4">
        <CompanyAvatar logoUrl={logoUrl} companyName={companyName} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">{job.title}</h1>
            {isEntryLevel && <Badge variant="ready" className="text-xs">Vaga de entrada</Badge>}
          </div>
          <p className="text-muted-foreground">{companyName}</p>
        </div>
      </div>

      {/* Meta row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
        {location && (
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />{location}
          </span>
        )}
        {publishedAgo && (
          <span className="inline-flex items-center gap-1.5">
            Publicada {publishedAgo}
          </span>
        )}
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2">
        <Badge className="text-sm"><Briefcase className="h-3 w-3 mr-1" />{employmentLabel}</Badge>
        {job.work_model && <Badge variant="secondary" className="text-sm">{workModelLabels[job.work_model] || job.work_model}</Badge>}
        {job.partner_orgs?.industry && <Badge variant="outline" className="text-sm">{job.partner_orgs.industry}</Badge>}
      </div>
    </div>
  );
}
